import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Textarea, toast, type IconName } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { DECISION_NOTE_MAX, decisionBody, decisionSchema, type DecisionValues } from "./bookings-model";
import type { Booking } from "./lead-types";
import { useDecideBooking, type Decision } from "./use-bookings";

const P = "engage.bookings.decision";
const NOT_FOUND = 404;
const CONFLICT = 409;
const FORM_ID = "booking-decision-form";

interface DecisionCopy {
  title: string;
  body: string;
  hint: string;
  submit: string;
  /** The button that closes the dialog without doing anything. */
  dismiss: string;
  /** Toast after the API accepted the decision. */
  done: string;
  icon: IconName;
  variant: "primary" | "danger";
}

/** Catalog keys of each decision (the dialog is the same; only the words, the icon and the button's weight change). */
const COPY: Readonly<Record<Decision, DecisionCopy>> = {
  confirm: { title: `${P}.confirmTitle`, body: `${P}.confirmBody`, hint: `${P}.confirmHint`, submit: `${P}.submitConfirm`, dismiss: "common.cancel", done: "engage.bookings.confirmed", icon: "check", variant: "primary" },
  decline: { title: `${P}.declineTitle`, body: `${P}.declineBody`, hint: `${P}.declineHint`, submit: `${P}.submitDecline`, dismiss: "common.cancel", done: "engage.bookings.declined", icon: "x-circle", variant: "danger" },
  cancel: { title: `${P}.cancelTitle`, body: `${P}.cancelBody`, hint: `${P}.cancelHint`, submit: `${P}.submitCancel`, dismiss: `${P}.keep`, done: "engage.bookings.cancelled", icon: "x-circle", variant: "danger" },
};

interface BookingDecisionDialogProps {
  booking: Booking;
  decision: Decision;
  /** "Mon, Oct 19 · 9:00 AM – 9:30 AM" in the workspace time zone. */
  when: string;
  onClose: () => void;
}

/** Confirm or decline a requested booking, or cancel a confirmed one, with an optional note (max 500). Mount it per decision. */
export function BookingDecisionDialog({ booking, decision, when, onClose }: BookingDecisionDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const decide = useDecideBooking();
  const tooLong = t(`${P}.tooLong`, { count: DECISION_NOTE_MAX });
  const form = useForm<DecisionValues>({ resolver: zodResolver(decisionSchema(tooLong)), defaultValues: { note: "" } });
  const copy = COPY[decision];
  const submit = form.handleSubmit((values) => {
    decide.mutate(
      { id: booking.id, decision, note: decisionBody(values).note },
      {
        onSuccess: () => {
          toast.success(t(copy.done, { name: booking.name }));
          onClose();
        },
        onError: (error) => {
          if (!isApiError(error) || (error.status !== CONFLICT && error.status !== NOT_FOUND)) return;
          // Someone else answered or cancelled first: the list refreshes behind the dialog.
          toast.error(message(error));
          onClose();
        },
      },
    );
  });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(copy.title)}
        description={t(copy.body, { name: booking.name, when })}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>{t(copy.dismiss)}</Button>
            <Button type="submit" form={FORM_ID} variant={copy.variant} loading={decide.isPending}>
              <Icon name={copy.icon} />
              {t(copy.submit)}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} noValidate className="space-y-3" onSubmit={(event) => void submit(event)}>
          {decide.isError ? <FormAlert title={message(decide.error)} /> : null}
          <Field label={t(`${P}.noteLabel`)} hint={t(copy.hint)} error={form.formState.errors.note?.message}>
            <Textarea rows={3} {...form.register("note")} />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
